/**
 * Patrols List Page - View patrol history with stats, filters, and table
 */

import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  makeStyles,
  Text,
  Button,
  Input,
  Badge,
  Tooltip,
  tokens,
  Spinner,
} from '@fluentui/react-components';
import {
  SearchRegular,
  PersonWalkingRegular,
  CheckmarkCircleRegular,
  ClockRegular,
  EyeRegular,
  DismissRegular,
  ArrowDownloadRegular,
  CalendarRegular,
  TimerRegular,
  CheckboxCheckedRegular,
} from '@fluentui/react-icons';
import { ContentCard } from '../../../components/layout';
import { PermissionGate, SearchableDropdown, TablePagination } from '../../../components/common';
import { usePagination } from '../../../hooks/usePagination';
import { ApiClient, newToast, fullDateTime } from '../../../configurations/index.config';
import type { Patrol, PatrolSummary, Site, Client, Zone } from '../../../types';
import { getErrorMessage } from '../../../types';

const useStyles = makeStyles({
  container: {
    padding: '24px',
  },
  pageHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '24px',
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  },
  headerIcon: {
    fontSize: '32px',
    color: tokens.colorBrandForeground1,
  },
  headerText: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  pageTitle: {
    fontSize: '24px',
    fontWeight: '600',
    color: '#323130',
  },
  pageSubtitle: {
    fontSize: '14px',
    color: '#605e5c',
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(6, 1fr)',
    gap: '16px',
    marginBottom: '24px',
  },
  statCard: {
    padding: '20px',
    backgroundColor: '#ffffff',
    borderRadius: '8px',
    border: '1px solid #e1dfdd',
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  },
  statIcon: {
    width: '48px',
    height: '48px',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '24px',
  },
  statContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  statValue: {
    fontSize: '28px',
    fontWeight: '600',
    color: '#323130',
  },
  statLabel: {
    fontSize: '13px',
    color: '#605e5c',
  },
  filterSection: {
    marginBottom: '24px',
  },
  filterGrid: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  filterRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: '12px',
    alignItems: 'end',
  },
  filterRowSecond: {
    display: 'grid',
    gridTemplateColumns: '150px 150px 150px 1fr auto',
    gap: '12px',
    alignItems: 'end',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  tableHeader: {
    backgroundColor: '#f5f5f5',
    borderBottom: '2px solid #e1dfdd',
  },
  th: {
    padding: '12px 16px',
    textAlign: 'left',
    fontSize: '13px',
    fontWeight: '600',
    color: '#323130',
  },
  thCenter: {
    padding: '12px 16px',
    textAlign: 'center',
    fontSize: '13px',
    fontWeight: '600',
    color: '#323130',
  },
  tr: {
    borderBottom: '1px solid #e1dfdd',
    ':hover': {
      backgroundColor: '#f9f9f9',
    },
  },
  td: {
    padding: '12px 16px',
    fontSize: '14px',
    color: '#323130',
  },
  tdCenter: {
    padding: '12px 16px',
    fontSize: '14px',
    color: '#323130',
    textAlign: 'center',
  },
  emptyState: {
    padding: '60px 20px',
    textAlign: 'center',
    color: '#605e5c',
  },
  actionButtons: {
    display: 'flex',
    gap: '8px',
  },
  employeeInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  employeeName: {
    fontWeight: '500',
  },
  siteClient: {
    fontSize: '12px',
    color: '#605e5c',
  },
  completionBar: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  progressBarContainer: {
    width: '60px',
    height: '6px',
    backgroundColor: '#e1dfdd',
    borderRadius: '3px',
    overflow: 'hidden',
  },
  progressBar: {
    height: '100%',
    borderRadius: '3px',
  },
  checkpointCount: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '2px',
  },
  checkpointFraction: {
    fontWeight: '500',
  },
  checkpointLabel: {
    fontSize: '11px',
    color: '#605e5c',
  },
});

export const PatrolsFluentUI: React.FC = () => {
  const styles = useStyles();
  const navigate = useNavigate();

  const [patrols, setPatrols] = useState<Patrol[]>([]);
  const [zones, setZones] = useState<Zone[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [sites, setSites] = useState<Site[]>([]);
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState<PatrolSummary['summary'] | null>(null);

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [filterZone, setFilterZone] = useState('');
  const [filterClient, setFilterClient] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterScheduleStatus, setFilterScheduleStatus] = useState('all');
  const [filterSite, setFilterSite] = useState('');
  const [filterStartDate, setFilterStartDate] = useState('');
  const [filterEndDate, setFilterEndDate] = useState('');

  useEffect(() => {
    fetchPatrols();
    fetchSummary();
    fetchZones();
    fetchClients();
    fetchSites();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchPatrols = async () => {
    setLoading(true);
    try {
      const params: Record<string, string> = {};
      if (filterSite) {
        params.site_id = filterSite;
      }
      if (filterStatus && filterStatus !== 'all') {
        params.status = filterStatus;
      }
      if (filterScheduleStatus && filterScheduleStatus !== 'all') {
        params.schedule_status = filterScheduleStatus;
      }
      if (filterStartDate) {
        params.start_date = filterStartDate;
      }
      if (filterEndDate) {
        params.end_date = filterEndDate;
      }

      const queryString = new URLSearchParams(params).toString();
      const response = await ApiClient.get(`/get-patrols${queryString ? `?${queryString}` : ''}`);

      if (response.data.status === 'success') {
        setPatrols(response.data.data || []);
      } else {
        newToast('error', response.data.message || 'Failed to fetch patrols');
      }
    } catch (error: unknown) {
      newToast('error', getErrorMessage(error));
    }
    setLoading(false);
  };

  const fetchSummary = async () => {
    try {
      const params: Record<string, string> = {};
      if (filterStartDate) {
        params.start_date = filterStartDate;
      }
      if (filterEndDate) {
        params.end_date = filterEndDate;
      }

      const queryString = new URLSearchParams(params).toString();
      const response = await ApiClient.get(
        `/get-patrol-summary${queryString ? `?${queryString}` : ''}`
      );

      if (response.data.status === 'success') {
        setSummary(response.data.data?.summary || null);
      }
    } catch (error: unknown) {
      console.error('Failed to fetch patrol summary:', error);
    }
  };

  const fetchZones = async () => {
    try {
      const response = await ApiClient.get('/get-zones');
      if (response.data.status === 'success') {
        setZones(response.data.data || []);
      }
    } catch (error: unknown) {
      console.error('Error fetching zones:', error);
    }
  };

  const fetchClients = async () => {
    try {
      const response = await ApiClient.get('/get-clients');
      if (response.data.status === 'success') {
        setClients(response.data.data || []);
      }
    } catch (error: unknown) {
      console.error('Error fetching clients:', error);
    }
  };

  const fetchSites = async () => {
    try {
      const response = await ApiClient.get('/get-sites');
      if (response.data.status === 'success') {
        setSites(response.data.data || []);
      }
    } catch (error: unknown) {
      console.error('Error fetching sites:', error);
    }
  };

  const applyFilters = () => {
    fetchPatrols();
    fetchSummary();
  };

  const filteredPatrols = useMemo(() => {
    let filtered = [...patrols];

    // Search filter (client-side for quick search)
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        patrol =>
          patrol.employee_name?.toLowerCase().includes(query) ||
          patrol.site_name?.toLowerCase().includes(query) ||
          patrol.client_name?.toLowerCase().includes(query)
      );
    }

    return filtered;
  }, [patrols, searchQuery]);

  const { paginatedData, currentPage, itemsPerPage, totalItems, setCurrentPage, setItemsPerPage } =
    usePagination<Patrol>({ data: filteredPatrols, initialItemsPerPage: 15 });

  const clearFilters = () => {
    setSearchQuery('');
    setFilterZone('');
    setFilterClient('');
    setFilterStatus('all');
    setFilterScheduleStatus('all');
    setFilterSite('');
    setFilterStartDate('');
    setFilterEndDate('');
    // Refetch with cleared filters
    setTimeout(() => {
      fetchPatrols();
      fetchSummary();
    }, 0);
  };

  const hasActiveFilters =
    searchQuery !== '' ||
    filterZone !== '' ||
    filterClient !== '' ||
    filterStatus !== 'all' ||
    filterScheduleStatus !== 'all' ||
    filterSite !== '' ||
    filterStartDate !== '' ||
    filterEndDate !== '';

  const getScheduleStatusBadge = (scheduleStatus?: string) => {
    switch (scheduleStatus) {
      case 'on_schedule':
        return (
          <Badge appearance="filled" color="success">
            🟢 On Schedule
          </Badge>
        );
      case 'off_schedule':
        return (
          <Badge appearance="filled" color="warning">
            🟠 Off Schedule
          </Badge>
        );
      case 'no_window':
        return (
          <Badge appearance="outline" color="informative">
            ⚪ No Window
          </Badge>
        );
      default:
        return (
          <Badge appearance="outline" color="informative">
            —
          </Badge>
        );
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'completed':
        return (
          <Badge appearance="filled" color="success">
            Completed
          </Badge>
        );
      case 'incomplete':
        return (
          <Badge appearance="filled" color="warning">
            Incomplete
          </Badge>
        );
      case 'cancelled':
        return (
          <Badge appearance="outline" color="danger">
            Cancelled
          </Badge>
        );
      default:
        return (
          <Badge appearance="outline" color="informative">
            {status}
          </Badge>
        );
    }
  };

  const getCompletionColor = (percentage: number) => {
    if (percentage >= 90) {
      return '#107c10';
    }
    if (percentage >= 70) {
      return '#ffaa44';
    }
    return '#d13438';
  };

  const handleViewPatrol = (patrol: Patrol) => {
    navigate(`/account/security-operations/patrols/${patrol.id}`);
  };

  return (
    <PermissionGate privilege="get-patrols">
      <div className={styles.container}>
        {/* Page Header */}
        <div className={styles.pageHeader}>
          <div className={styles.headerLeft}>
            <PersonWalkingRegular className={styles.headerIcon} />
            <div className={styles.headerText}>
              <Text className={styles.pageTitle}>Patrol History</Text>
              <Text className={styles.pageSubtitle}>View and analyze guard patrol activities</Text>
            </div>
          </div>
          <Button
            appearance="subtle"
            icon={<ArrowDownloadRegular />}
            onClick={() => newToast('info', 'Export feature coming soon')}
          >
            Export
          </Button>
        </div>

        {/* Stats Cards */}
        <div className={styles.statsGrid}>
          <div className={styles.statCard}>
            <div
              className={styles.statIcon}
              style={{ backgroundColor: '#e8f4fd', color: '#0078d4' }}
            >
              <PersonWalkingRegular />
            </div>
            <div className={styles.statContent}>
              <Text className={styles.statValue}>{summary?.total_patrols ?? 0}</Text>
              <Text className={styles.statLabel}>Total Patrols</Text>
            </div>
          </div>

          <div className={styles.statCard}>
            <div
              className={styles.statIcon}
              style={{ backgroundColor: '#dff6dd', color: '#107c10' }}
            >
              <CheckmarkCircleRegular />
            </div>
            <div className={styles.statContent}>
              <Text className={styles.statValue}>{summary?.completed_patrols ?? 0}</Text>
              <Text className={styles.statLabel}>Completed</Text>
            </div>
          </div>

          <div className={styles.statCard}>
            <div
              className={styles.statIcon}
              style={{ backgroundColor: '#fff4ce', color: '#ffaa44' }}
            >
              <ClockRegular />
            </div>
            <div className={styles.statContent}>
              <Text className={styles.statValue}>{summary?.incomplete_patrols ?? 0}</Text>
              <Text className={styles.statLabel}>Incomplete</Text>
            </div>
          </div>

          <div className={styles.statCard}>
            <div
              className={styles.statIcon}
              style={{ backgroundColor: '#e8f4fd', color: '#0078d4' }}
            >
              <CheckboxCheckedRegular />
            </div>
            <div className={styles.statContent}>
              <Text className={styles.statValue}>
                {summary?.avg_completion_percentage?.toFixed(1) ?? 0}%
              </Text>
              <Text className={styles.statLabel}>Avg Completion</Text>
            </div>
          </div>

          <div className={styles.statCard}>
            <div
              className={styles.statIcon}
              style={{ backgroundColor: '#f3e8fd', color: '#8764b8' }}
            >
              <TimerRegular />
            </div>
            <div className={styles.statContent}>
              <Text className={styles.statValue}>
                {summary?.avg_duration_minutes?.toFixed(0) ?? 0}
              </Text>
              <Text className={styles.statLabel}>Avg Duration (min)</Text>
            </div>
          </div>

          <div className={styles.statCard}>
            <div
              className={styles.statIcon}
              style={{ backgroundColor: '#dff6dd', color: '#107c10' }}
            >
              <CalendarRegular />
            </div>
            <div className={styles.statContent}>
              <Text className={styles.statValue}>{summary?.completion_rate?.toFixed(1) ?? 0}%</Text>
              <Text className={styles.statLabel}>Completion Rate</Text>
            </div>
          </div>
        </div>

        {/* Filters & Table */}
        <ContentCard>
          <div className={styles.filterSection}>
            <div className={styles.filterGrid}>
              {/* Row 1: Zone, Client, Site, Status */}
              <div className={styles.filterRow}>
                <SearchableDropdown
                  placeholder="All Zones"
                  options={[
                    { id: '', label: 'All Zones' },
                    ...zones.map(z => ({
                      id: z.id.toString(),
                      label: z.name || 'Unknown Zone',
                    })),
                  ]}
                  selectedValue={filterZone}
                  onSelect={setFilterZone}
                />

                <SearchableDropdown
                  placeholder="All Clients"
                  options={[
                    { id: '', label: 'All Clients' },
                    ...clients.map(c => ({
                      id: c.id.toString(),
                      label: c.client_name || 'Unknown Client',
                    })),
                  ]}
                  selectedValue={filterClient}
                  onSelect={setFilterClient}
                />

                <SearchableDropdown
                  placeholder="All Sites"
                  options={[
                    { id: '', label: 'All Sites' },
                    ...sites.map(s => ({
                      id: s.id.toString(),
                      label: s.site_name || s.name || 'Unknown Site',
                    })),
                  ]}
                  selectedValue={filterSite}
                  onSelect={setFilterSite}
                />

                <SearchableDropdown
                  placeholder="Status"
                  options={[
                    { id: 'all', label: 'All Status' },
                    { id: 'completed', label: 'Completed' },
                    { id: 'incomplete', label: 'Incomplete' },
                    { id: 'cancelled', label: 'Cancelled' },
                  ]}
                  selectedValue={filterStatus}
                  onSelect={setFilterStatus}
                />
              </div>

              {/* Row 1.5: Schedule Status Filter */}
              <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                <SearchableDropdown
                  placeholder="Schedule Status"
                  options={[
                    { id: 'all', label: 'All Schedule' },
                    { id: 'on_schedule', label: '🟢 On Schedule' },
                    { id: 'off_schedule', label: '🟠 Off Schedule' },
                    { id: 'no_window', label: '⚪ No Window' },
                  ]}
                  selectedValue={filterScheduleStatus}
                  onSelect={setFilterScheduleStatus}
                />
              </div>

              {/* Row 2: Start Date, End Date, Search, Apply/Clear */}
              <div className={styles.filterRowSecond}>
                <Input
                  type="date"
                  value={filterStartDate}
                  onChange={(e, data) => setFilterStartDate(data.value)}
                />

                <Input
                  type="date"
                  value={filterEndDate}
                  onChange={(e, data) => setFilterEndDate(data.value)}
                />

                <Input
                  placeholder="Search employee, site, client..."
                  contentBefore={<SearchRegular />}
                  value={searchQuery}
                  onChange={(e, data) => setSearchQuery(data.value)}
                />

                <div style={{ display: 'flex', gap: '8px' }}>
                  <Button appearance="primary" onClick={applyFilters}>
                    Apply
                  </Button>
                  {hasActiveFilters && (
                    <Button appearance="subtle" icon={<DismissRegular />} onClick={clearFilters}>
                      Clear
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Table */}
          {loading ? (
            <div className={styles.emptyState}>
              <Spinner label="Loading patrols..." />
            </div>
          ) : filteredPatrols.length === 0 ? (
            <div className={styles.emptyState}>
              <PersonWalkingRegular
                style={{ fontSize: '48px', color: '#c8c6c4', marginBottom: '16px' }}
              />
              <Text size={500} weight="semibold" style={{ display: 'block' }}>
                No Patrols Found
              </Text>
              <Text style={{ marginTop: '8px', display: 'block' }}>
                {hasActiveFilters
                  ? 'Try adjusting your filters'
                  : 'Patrol records will appear here once guards complete patrols'}
              </Text>
            </div>
          ) : (
            <>
              <table className={styles.table}>
                <thead className={styles.tableHeader}>
                  <tr>
                    <th className={styles.th}>Employee</th>
                    <th className={styles.th}>Site / Client</th>
                    <th className={styles.th}>Shift</th>
                    <th className={styles.th}>Date & Time</th>
                    <th className={styles.thCenter}>Duration</th>
                    <th className={styles.thCenter}>Checkpoints</th>
                    <th className={styles.thCenter}>Completion</th>
                    <th className={styles.th}>Status</th>
                    <th className={styles.th}>Schedule</th>
                    <th className={styles.th}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedData.map((patrol: Patrol) => (
                    <tr key={patrol.id} className={styles.tr}>
                      <td className={styles.td}>
                        <div className={styles.employeeInfo}>
                          <span className={styles.employeeName}>{patrol.employee_name}</span>
                          {patrol.face_verified && (
                            <Badge appearance="outline" color="success" size="small">
                              Face Verified
                            </Badge>
                          )}
                        </div>
                      </td>
                      <td className={styles.td}>
                        <div className={styles.employeeInfo}>
                          <span className={styles.employeeName}>{patrol.site_name}</span>
                          <span className={styles.siteClient}>{patrol.client_name}</span>
                        </div>
                      </td>
                      <td className={styles.td}>{patrol.shift_name || '—'}</td>
                      <td className={styles.td}>
                        {patrol.started_at ? fullDateTime(patrol.started_at) : '—'}
                      </td>
                      <td className={styles.tdCenter}>
                        {patrol.duration_minutes ? `${patrol.duration_minutes} min` : '—'}
                      </td>
                      <td className={styles.tdCenter}>
                        <div className={styles.checkpointCount}>
                          <span className={styles.checkpointFraction}>
                            {patrol.scanned_checkpoints}/{patrol.expected_checkpoints}
                          </span>
                          <span className={styles.checkpointLabel}>scanned</span>
                        </div>
                      </td>
                      <td className={styles.tdCenter}>
                        <div className={styles.completionBar}>
                          <div className={styles.progressBarContainer}>
                            <div
                              className={styles.progressBar}
                              style={{
                                width: `${Math.min(patrol.completion_percentage || 0, 100)}%`,
                                backgroundColor: getCompletionColor(
                                  patrol.completion_percentage || 0
                                ),
                              }}
                            />
                          </div>
                          <Text
                            style={{
                              color: getCompletionColor(patrol.completion_percentage || 0),
                              fontWeight: 500,
                            }}
                          >
                            {patrol.completion_percentage?.toFixed(0) || 0}%
                          </Text>
                        </div>
                      </td>
                      <td className={styles.td}>{getStatusBadge(patrol.status)}</td>
                      <td className={styles.td}>
                        {getScheduleStatusBadge(patrol.schedule_status)}
                        {patrol.matched_window_name && (
                          <Text
                            style={{
                              fontSize: '11px',
                              color: '#605e5c',
                              display: 'block',
                              marginTop: '2px',
                            }}
                          >
                            {patrol.matched_window_name}
                          </Text>
                        )}
                      </td>
                      <td className={styles.td}>
                        <div className={styles.actionButtons}>
                          <Tooltip content="View Details" relationship="label">
                            <Button
                              appearance="subtle"
                              icon={<EyeRegular />}
                              size="small"
                              onClick={() => handleViewPatrol(patrol)}
                            />
                          </Tooltip>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <TablePagination
                totalItems={totalItems}
                currentPage={currentPage}
                itemsPerPage={itemsPerPage}
                onPageChange={setCurrentPage}
                onItemsPerPageChange={setItemsPerPage}
              />
            </>
          )}
        </ContentCard>
      </div>
    </PermissionGate>
  );
};

export default PatrolsFluentUI;
